// ============================================================================
// ReviewLabelPicker — pick the operator's mark for one job, or for a selection.
// ----------------------------------------------------------------------------
// WHY A PORTAL, non-negotiably. The trigger lives inside the Run Sheet's
// preview panel, whose `.rs-row > *` ancestors are `overflow: hidden` and whose
// `.rs-files` sibling sits at z-index 5. A popover rendered inline is clipped
// by the first and covered by the second. So: createPortal to document.body,
// position: fixed, clamped to the viewport. The mechanics here are lifted
// wholesale from ScopeAttributionDrillDown, which already paid for every one of
// these lessons in production.
//
// ACCESSIBILITY NOTES THAT ARE LOAD-BEARING, NOT DECORATION:
//   * `aria-activedescendant` drives the highlight, NOT roving tabIndex. The
//     global stylesheet paints a `[tabindex]:focus-visible` ring, so moving
//     real focus down a list would stamp an orange box on every arrow press.
//   * Every option renders its NAME as text beside the tone dot. Colour is
//     never the only channel (WCAG 1.4.1) — and an operator who picked two
//     blue-ish labels would otherwise have no way to tell them apart.
//   * The capture-phase scroll listener IGNORES scrolls originating inside the
//     preview panel's own scroll container. Without that exclusion the popover
//     slams shut the instant the panel behind it moves, which is a bug this
//     repo has already shipped once.
// ============================================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icons } from '../ui/Icon';
import OperationalFieldBoundary from '../ui/OperationalFieldBoundary';
import ToneSwatches, { nextUnusedTone } from '../ui/ToneSwatches';
import { TONES, resolveTone, type ReviewLabel, type ReviewLabelTone } from '../../services/review-labels';

const WIDTH = 240;
const GAP = 6;
/**
 * A DECISION THRESHOLD ONLY: how much room must exist below the trigger before
 * the panel opens downwards. It is deliberately NOT a position input any more.
 *
 * It used to do double duty as the assumed panel height in the flip-above
 * branch (`top = rect.top - GAP - MIN_SPACE_BELOW`), which meant the number had
 * to equal the real height or the panel drifted. Raising it to 340 to cover the
 * create block therefore pinned a ~187px panel 346px above its trigger, leaving
 * a ~159px gap: a popover floating in the middle of the screen with no visible
 * connection to the control that opened it. Placement now anchors the panel's
 * BOTTOM edge instead, so the geometry is exact at any height and this constant
 * can be tuned freely.
 */
const MIN_SPACE_BELOW = 340;
const VIEWPORT_MARGIN = 8;

export interface ReviewLabelPickerProps {
    labels: ReviewLabel[];
    /** Currently applied label id, or null. Bulk mode passes null. */
    value: string | null;
    /** Heading above the option list. "Mark this job" / "Mark 3 jobs". */
    heading: string;
    /** Called with the chosen id, or null for the explicit "No label" option. */
    onPick: (labelId: string | null) => void;
    /** Closes the popover and opens Settings on the Labels tab. */
    onManage?: () => void;
    /**
     * Create a label AND apply it to whatever this picker targets, in one
     * gesture. The picker deliberately does NOT do the I/O: DashboardPage owns
     * `labels`, `labelOverrides` and the toast, and a label that is not in
     * `labels` before the assign lands renders a BLANK chip on the sheet
     * (labelById.get() misses, so labelName is '') and sorts as unlabelled.
     *
     * Resolves rather than throws, so a failure is a message this panel can
     * render inline beside the field the operator is still looking at.
     *
     * Omit it and there is no create affordance at all, and the zero-label state
     * falls back to prose. That keeps the picker usable from any call site that
     * has no create handler.
     */
    onCreate?: (input: { name: string; tone: ReviewLabelTone })
        => Promise<{ ok: true } | { ok: false; message: string }>;
    /** How many jobs the create will mark. Drives the button copy so it never
     *  promises a mark it will not make (bulk mode can render at 0 selected). */
    createTargetCount?: number;
    /**
     * Rename a label in place. Same resolve-never-throw contract as onCreate.
     *
     * The sheet resolves every chip's NAME through the live definition list, so
     * a rename that lands in `labels` re-letters every row carrying it with no
     * further work. That is why only the id and the new name travel.
     */
    onRename?: (id: string, name: string)
        => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * Delete a label. The parent owns the fallout, which is the whole reason
     * this is a callback and not a fetch: deleting has to take the mark off
     * every row that carried it, drop the id out of the FILTER (a stranded id
     * empties the sheet with no visible cause) and out of the optimistic
     * override map. `usageCount` is on the LIST response precisely so the
     * confirm can name a real number before the row is gone.
     */
    onDelete?: (id: string)
        => Promise<{ ok: true } | { ok: false; message: string }>;
    /**
     * Re-read the definition list, called when edit mode opens.
     *
     * `usageCount` rides the LIST response and is what the delete confirm names.
     * A label created in THIS session was appended locally with `usageCount: 0`
     * and never refreshed, so without this the confirm on a label the operator
     * just applied to three jobs offers a bare "Delete" and quietly drops the
     * number it exists to show. A destructive confirm that cannot count is worse
     * than no count at all, because the operator reads the absence as zero.
     */
    onRefreshLabels?: () => Promise<void> | void;
    /** Rendered as the trigger's contents. */
    children: React.ReactNode;
    triggerClassName?: string;
    triggerTitle?: string;
    triggerKind?: string;
}

/** Exactly one of top/bottom is set. Anchoring by BOTTOM when the panel opens
 *  upwards keeps it glued to its trigger for ANY panel height, instead of
 *  assuming a height and leaving a gap when the guess is wrong. */
interface Placement { top?: number; bottom?: number; left: number }

export default function ReviewLabelPicker({
    labels,
    value,
    heading,
    onPick,
    onManage,
    onCreate,
    createTargetCount = 1,
    onRename,
    onDelete,
    onRefreshLabels,
    children,
    triggerClassName = 'rs-find__chip qm-inline-action-btn',
    triggerTitle,
    triggerKind = 'label',
}: ReviewLabelPickerProps) {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState<Placement | null>(null);
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const listRef = useRef<HTMLDivElement | null>(null);
    const nameInputRef = useRef<HTMLInputElement | null>(null);

    // ── The create block ────────────────────────────────────────────────────
    // `creating` is the EXPANDED flag, `busy` the in-flight flag. They are
    // separate because the block stays open and keeps the typed name while a
    // create is in flight, and again if it fails.
    const canCreate = typeof onCreate === 'function';
    const [creating, setCreating] = useState(false);
    const [draftName, setDraftName] = useState('');
    const [draftTone, setDraftTone] = useState<ReviewLabelTone>('slate');
    const [busy, setBusy] = useState(false);
    const [createError, setCreateError] = useState('');

    // With no labels at all there is nothing to pick, so the create field IS the
    // panel. Anything else and the operator has to press a button to reach the
    // only thing this panel can usefully do for them.
    const forcedOpen = canCreate && labels.length === 0;
    const showCreate = canCreate && (creating || forcedOpen);

    // ── Edit mode ───────────────────────────────────────────────────────────
    // A MODE, not per-option buttons. `role="option"` must not contain focusable
    // children, so a pencil and a bin inside each row would break the listbox
    // for every assistive technology in exchange for saving one click. Editing
    // swaps the listbox out for a plain list of fields instead, which leaves the
    // picking path's ARIA contract exactly as it was.
    //
    // Renaming and deleting only; re-toning and reordering stay in Settings,
    // which remains the comprehensive surface.
    const canEdit = typeof onRename === 'function' || typeof onDelete === 'function';
    const [editing, setEditing] = useState(false);
    const [editBusyId, setEditBusyId] = useState<string | null>(null);
    const [editError, setEditError] = useState('');
    /** The label the bin was pressed on. A second press on the same row is the
     *  confirm, so a destructive action still takes two deliberate gestures
     *  without a modal that would fight this popover's dismiss handler. */
    const [pendingDelete, setPendingDelete] = useState<string | null>(null);
    const showEdit = canEdit && editing && labels.length > 0;

    // The option list, with the explicit "No label" entry first. An explicit
    // clear inside the list matters: without it the only way to remove a mark
    // is the separate Clear button, which does not exist in bulk mode.
    const options: Array<{ id: string | null; name: string; tone: string }> = [
        { id: null, name: 'No label', tone: 'slate' },
        ...labels.map(l => ({ id: l.id, name: l.name, tone: resolveTone(l.tone) })),
    ];

    // Which option the keyboard is ON. An INDEX rather than an id, because
    // `null` is a legitimate option id here and would be indistinguishable from
    // "nothing is active".
    const [activeIndex, setActiveIndex] = useState(0);

    const close = useCallback((returnFocus = true) => {
        setOpen(false);
        setPos(null);
        // The draft dies with the panel. A half-typed name resurrected on the
        // next open would be a name the operator had already walked away from,
        // and on a DIFFERENT job than the one they typed it for.
        setCreating(false);
        setDraftName('');
        setCreateError('');
        setBusy(false);
        // An armed delete must never survive the panel. Reopening onto a
        // primed bin would turn the next click into a destructive one the
        // operator did not arm.
        setEditing(false);
        setPendingDelete(null);
        setEditError('');
        setEditBusyId(null);
        if (returnFocus) { try { triggerRef.current?.focus(); } catch { /* detached */ } }
    }, []);

    const openAt = useCallback(() => {
        const btn = triggerRef.current;
        if (!btn) return;
        const rect = btn.getBoundingClientRect();
        const vw = typeof window !== 'undefined' ? window.innerWidth : 1280;
        const vh = typeof window !== 'undefined' ? window.innerHeight : 800;

        // Clamp so the panel never runs off the right edge. The preview panel
        // sits hard against it, so this fires on essentially every open at a
        // laptop width — it is the normal case, not an edge case.
        let left = rect.left;
        if (left + WIDTH > vw - VIEWPORT_MARGIN) left = Math.max(VIEWPORT_MARGIN, vw - WIDTH - VIEWPORT_MARGIN);

        // Opening UPWARDS anchors the panel's BOTTOM edge, never its top.
        //
        // It used to compute `top = rect.top - GAP - MIN_SPACE_BELOW`, which
        // silently assumed the panel was exactly MIN_SPACE_BELOW tall. It is
        // not: it measures ~187px against a 340px reservation, so the panel was
        // pinned 346px above the trigger and its bottom edge landed ~159px short
        // of it. The operator saw a popover floating in the middle of nowhere
        // with no visible connection to the control they clicked.
        //
        // Anchoring by `bottom` makes the geometry exact for ANY height, so the
        // panel hugs its trigger whether it is showing three labels or an open
        // create block, and MIN_SPACE_BELOW goes back to being only a decision
        // about which way to open.
        // Flip up only when below is BOTH short of ideal and genuinely roomier
        // above. Testing `spaceBelow < MIN_SPACE_BELOW` alone flips into a
        // SMALLER space whenever the threshold exceeds the real panel height: at
        // a 420px viewport with the trigger near the top, 290px below still lost
        // to 100px above, and a 187px panel hung 93px off the top of the screen.
        // Comparing the two sides makes the choice self-correcting, so the
        // threshold can be generous without ever choosing the worse side.
        const spaceBelow = vh - rect.bottom - GAP - VIEWPORT_MARGIN;
        const spaceAbove = rect.top - GAP - VIEWPORT_MARGIN;
        const flipUp = spaceBelow < MIN_SPACE_BELOW && spaceAbove > spaceBelow;
        const placement: Placement = flipUp
            ? { bottom: Math.max(VIEWPORT_MARGIN, vh - rect.top + GAP), left }
            : { top: rect.bottom + GAP, left };

        // Open ON the current value, so Enter re-picks what is already there
        // rather than silently changing it.
        const current = options.findIndex(o => o.id === value);
        setActiveIndex(current >= 0 ? current : 0);
        // Armed ONCE per open. Recomputing while the block is live would
        // overwrite an explicit pick the moment the name is backspaced away.
        setDraftTone(nextUnusedTone(labels));
        setPos(placement);
        setOpen(true);
    }, [options, value, labels]);

    // Focus the LIST once per open, in an effect rather than an inline ref
    // callback: an inline arrow changes identity every render, so React would
    // detach and re-attach (and therefore re-focus) on every arrow press.
    //
    // Zero labels means there is no list to focus and nothing to pick, so the
    // create field takes it instead. Before this the popover opened with focus
    // nowhere, which also left Escape dead in exactly that state.
    useEffect(() => {
        if (!open) return;
        try { (listRef.current ?? nameInputRef.current)?.focus(); } catch { /* detached */ }
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;

        const onDown = (e: MouseEvent) => {
            // BOTH nodes: the popover is in a portal, so a click inside it is
            // outside the trigger's subtree and would otherwise close it before
            // the option's own click ever fired.
            const inTrigger = triggerRef.current?.contains(e.target as Node);
            const inPopover = popoverRef.current?.contains(e.target as Node);
            if (!inTrigger && !inPopover) close(false);
        };
        const onScroll = (e: Event) => {
            // Ignore scrolls that START inside the preview panel or inside the
            // popover's own list. A bare capture-phase listener treats those as
            // "the page moved" and shuts the panel the moment the operator
            // scrolls anything at all.
            const t = e?.target as Node | null;
            if (t && popoverRef.current?.contains(t)) return;
            if (t && (t as Element)?.closest?.('.rs-preview__scroll')) return;
            close(false);
        };
        const onResize = () => close(false);

        document.addEventListener('mousedown', onDown);
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onResize);
        return () => {
            document.removeEventListener('mousedown', onDown);
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onResize);
        };
    }, [open, close]);

    // NOTE: this is bound to the popover ROOT, not to the list, and React
    // synthetic events bubble the COMPONENT tree. So every keystroke from every
    // descendant arrives here, including the ones typed into the name field.
    //
    // Escape sits ABOVE the target guard on purpose: it means the same thing
    // everywhere in this panel, including mid-create (WCAG 2.1.2 — an in-flight
    // round trip must never become a keyboard trap), and it also has to keep
    // working when there is no list at all to have raised it.
    //
    // Everything BELOW the guard is list navigation and must never fire for a
    // keystroke that originated in a text input or on a swatch. Without it the
    // Space in "Ready to send" hits the Enter/Space branch, marks the job with
    // whatever option happens to be active, and shuts the panel.
    const onListKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            close(true);
            return;
        }
        if (e.target !== listRef.current) return;
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const delta = e.key === 'ArrowDown' ? 1 : -1;
            setActiveIndex(i => {
                const n = options.length;
                return ((i + delta) % n + n) % n;
            });
            return;
        }
        if (e.key === 'Home') { e.preventDefault(); setActiveIndex(0); return; }
        if (e.key === 'End') { e.preventDefault(); setActiveIndex(options.length - 1); return; }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            const opt = options[activeIndex];
            if (opt !== undefined) { onPick(opt.id); close(true); }
        }
    };

    const listId = 'rs-labelpick-list';

    const trimmed = draftName.trim();

    const submitCreate = useCallback(async () => {
        if (!onCreate || busy) return;
        const name = draftName.trim();
        if (!name) return;
        setBusy(true);
        setCreateError('');
        const res = await onCreate({ name, tone: draftTone })
            .catch(() => ({ ok: false as const, message: 'Could not create that label. Nothing was saved.' }));
        // The panel may already be gone: Escape stays live during the round trip
        // by design, and the parent owns the promise, so the label is still
        // created and applied. Guard the state writes, not the outcome.
        if (res.ok) { close(true); return; }
        setBusy(false);
        setCreateError(res.message);
        try { nameInputRef.current?.focus(); } catch { /* detached */ }
    }, [onCreate, busy, draftName, draftTone, close]);

    const submitRename = useCallback(async (id: string, next: string, previous: string) => {
        const name = next.trim();
        // An empty field is a mis-edit, not an instruction to blank a label, and
        // the route would 400 on it anyway. Restore rather than complain.
        if (!name || name === previous || !onRename) return;
        setEditBusyId(id);
        setEditError('');
        const res = await onRename(id, name)
            .catch(() => ({ ok: false as const, message: 'Could not rename that label.' }));
        setEditBusyId(null);
        if (!res.ok) setEditError(res.message);
    }, [onRename]);

    const submitDelete = useCallback(async (id: string) => {
        if (!onDelete) return;
        setEditBusyId(id);
        setEditError('');
        const res = await onDelete(id)
            .catch(() => ({ ok: false as const, message: 'Could not delete that label.' }));
        setEditBusyId(null);
        setPendingDelete(null);
        if (!res.ok) setEditError(res.message);
    }, [onDelete]);

    /** Never promises a mark it will not make. Bulk mode can render at 0. */
    const createCopy = busy
        ? 'Creating...'
        : createTargetCount === 0 ? 'Create label'
            : createTargetCount === 1 ? 'Create and mark'
                : `Create and mark ${createTargetCount}`;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={triggerClassName}
                data-kind={triggerKind}
                aria-haspopup="listbox"
                aria-expanded={open}
                title={triggerTitle}
                onClick={() => (open ? close(true) : openAt())}
            >
                {children}
            </button>
            {open && pos && typeof document !== 'undefined' && createPortal(
                <OperationalFieldBoundary>
                <div
                    ref={popoverRef}
                    className="rs-labelpick"
                    style={{ position: 'fixed', top: pos.top, bottom: pos.bottom, left: pos.left, width: WIDTH }}
                    onKeyDown={onListKeyDown}
                >
                    <p className="rs-labelpick__head">{showEdit ? 'Edit labels' : heading}</p>
                    {showEdit ? (
                        <div className="rs-labelpick__edit">
                            {labels.map((l) => {
                                const tone = TONES[resolveTone(l.tone)];
                                const armed = pendingDelete === l.id;
                                const rowBusy = editBusyId === l.id;
                                const used = l.usageCount || 0;
                                return (
                                    <div className="rs-labelpick__editrow" key={l.id}>
                                        <i
                                            className="rs-labelpick__dot"
                                            aria-hidden="true"
                                            style={{ background: tone.fg, borderColor: tone.fg }}
                                        />
                                        <span className="rs-labelpick__field rs-labelpick__field--row">
                                            <input
                                                type="text"
                                                className="rs-labelpick__name-input qm-input-unthemed"
                                                defaultValue={l.name}
                                                maxLength={40}
                                                aria-label={`Rename ${l.name}`}
                                                readOnly={rowBusy}
                                                /* Commit on blur, like Settings.
                                                   Enter blurs rather than
                                                   submitting directly, so there
                                                   is exactly one commit path. */
                                                onBlur={(e) => void submitRename(l.id, e.target.value, l.name)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
                                                }}
                                            />
                                        </span>
                                        {onDelete && (
                                            <button
                                                type="button"
                                                className="rs-labelpick__del qm-inline-action-btn"
                                                data-armed={armed ? 'true' : 'false'}
                                                disabled={rowBusy}
                                                /* Two gestures, no modal. A
                                                   ConfirmModal renders in its own
                                                   portal, and a mousedown there
                                                   is outside this popover, so it
                                                   would dismiss the panel behind
                                                   its own dialog. */
                                                title={armed
                                                    ? (used ? `Delete, and take it off ${used} job${used === 1 ? '' : 's'}` : 'Delete this label')
                                                    : 'Delete this label'}
                                                aria-label={armed
                                                    ? `Confirm delete ${l.name}${used ? `, currently on ${used} job${used === 1 ? '' : 's'}` : ''}`
                                                    : `Delete ${l.name}`}
                                                onClick={() => (armed ? void submitDelete(l.id) : setPendingDelete(l.id))}
                                            >
                                                {armed
                                                    ? <span className="rs-labelpick__delconfirm">{used ? `Delete (${used})` : 'Delete'}</span>
                                                    : <Icons.Trash size={12} aria-hidden="true" />}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            {editError && <p className="rs-labelpick__err" role="alert">{editError}</p>}
                            <p className="rs-labelpick__hint">
                                Deleting takes the label off its jobs. It never deletes a job.
                            </p>
                        </div>
                    ) : labels.length === 0 ? (
                        /* No listbox at all: there is nothing to pick. When this
                           picker can create, the field below IS the panel and
                           already holds the focus, so prose here would be a
                           caption on an empty box. */
                        canCreate ? null : (
                            <p className="rs-labelpick__empty">
                                No labels yet. Create your first one in Settings, then it will show up here.
                            </p>
                        )
                    ) : (
                        <div
                            className="rs-labelpick__list"
                            data-compact={showCreate ? 'true' : 'false'}
                            role="listbox"
                            aria-multiselectable="false"
                            aria-label={heading}
                            aria-activedescendant={`${listId}-${activeIndex}`}
                            tabIndex={0}
                            // Focus lands on the LIST, not on an option. The
                            // global [tabindex]:focus-visible ring would
                            // otherwise stamp an orange box on each arrow press.
                            ref={listRef}
                        >
                            {options.map((o, i) => {
                                const tone = TONES[resolveTone(o.tone)];
                                return (
                                    <div
                                        key={o.id ?? '__none'}
                                        id={`${listId}-${i}`}
                                        role="option"
                                        aria-selected={o.id === value}
                                        className="rs-labelpick__opt"
                                        data-active={i === activeIndex ? 'true' : 'false'}
                                        data-on={o.id === value ? 'true' : 'false'}
                                        onMouseEnter={() => setActiveIndex(i)}
                                        onClick={() => { onPick(o.id); close(true); }}
                                    >
                                        <i
                                            className="rs-labelpick__dot"
                                            aria-hidden="true"
                                            /* "No label" gets a hollow ring, so the
                                               clear option cannot be mistaken for a
                                               grey label named "No label". */
                                            style={o.id === null
                                                ? { background: 'transparent', borderColor: tone.fg }
                                                : { background: tone.fg, borderColor: tone.fg }}
                                        />
                                        {/* The NAME, always. Colour alone would
                                            fail WCAG 1.4.1 and would also be
                                            unusable for two similar tones. */}
                                        <span className="rs-labelpick__name">{o.name}</span>
                                        {o.id === value && <Icons.Check size={12} aria-hidden="true" />}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    {showCreate && (
                        <div className="rs-labelpick__new">
                            {forcedOpen && (
                                <p className="rs-labelpick__hint">Labels are shared across your business.</p>
                            )}
                            {/* The WRAPPER owns the focus ring. qm-input-unthemed
                                strips the global input theme AND its focus
                                outline, so without this element the keyboard has
                                no visible focus state here at all (WCAG 2.4.11).
                                The two ship together, always. */}
                            <div className="rs-labelpick__field">
                                <input
                                    ref={nameInputRef}
                                    type="text"
                                    className="rs-labelpick__name-input qm-input-unthemed"
                                    value={draftName}
                                    /* Mirrors NAME_MAX in the route, so the
                                       operator meets the limit at the keyboard
                                       rather than as a 400 after pressing Enter. */
                                    maxLength={40}
                                    placeholder="Name this label"
                                    aria-label="New label name"
                                    /* readOnly, never disabled: a disabled input
                                       drops focus, which would move the keyboard
                                       somewhere unannounced mid-request. */
                                    readOnly={busy}
                                    onChange={(e) => { setDraftName(e.target.value); if (createError) setCreateError(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void submitCreate(); } }}
                                />
                            </div>
                            <ToneSwatches
                                value={draftTone}
                                onChange={setDraftTone}
                                idPrefix="rs-labelpick"
                                size={20}
                                disabled={busy}
                            />
                            {createError && (
                                <p className="rs-labelpick__err" role="alert">{createError}</p>
                            )}
                            <button
                                type="button"
                                className="rs-labelpick__create qm-inline-action-btn"
                                disabled={busy || !trimmed}
                                onClick={() => void submitCreate()}
                            >
                                {createCopy}
                            </button>
                        </div>
                    )}
                    {(canCreate || onManage || canEdit) && (
                        <div className="rs-labelpick__foot">
                            {showEdit ? (
                                <button
                                    type="button"
                                    className="rs-labelpick__manage qm-inline-action-btn"
                                    onClick={() => {
                                        setEditing(false);
                                        setPendingDelete(null);
                                        setEditError('');
                                        try { listRef.current?.focus(); } catch { /* detached */ }
                                    }}
                                >
                                    Done
                                </button>
                            ) : canCreate && (showCreate ? (
                                /* Cancel is the collapse gesture and a real
                                   button, not an Escape overload: Escape closes
                                   the whole panel, everywhere, one meaning. */
                                !forcedOpen && (
                                    <button
                                        type="button"
                                        className="rs-labelpick__manage qm-inline-action-btn"
                                        disabled={busy}
                                        onClick={() => {
                                            setCreating(false);
                                            setDraftName('');
                                            setCreateError('');
                                            try { listRef.current?.focus(); } catch { /* detached */ }
                                        }}
                                    >
                                        Cancel
                                    </button>
                                )
                            ) : (
                                /* Pressing this REPLACES it with the block, so
                                   nothing the operator just clicked moves under
                                   the cursor and it cannot be double-pressed. */
                                <button
                                    type="button"
                                    className="rs-labelpick__manage qm-inline-action-btn"
                                    onClick={() => {
                                        setCreating(true);
                                        // Focus after the block has mounted.
                                        setTimeout(() => { try { nameInputRef.current?.focus(); } catch { /* detached */ } }, 0);
                                    }}
                                >
                                    <Icons.Plus size={11} aria-hidden="true" />
                                    New label
                                </button>
                            ))}
                            {/* Rename and delete without leaving the sheet. Only
                                offered when there is something to edit. */}
                            {canEdit && !showEdit && !showCreate && labels.length > 0 && (
                                <button
                                    type="button"
                                    className="rs-labelpick__manage qm-inline-action-btn"
                                    onClick={() => {
                                        setEditing(true);
                                        setEditError('');
                                        // Live usageCount before any bin is
                                        // armed: the confirm names a number.
                                        void onRefreshLabels?.();
                                    }}
                                >
                                    Edit
                                </button>
                            )}
                            {onManage && !showEdit && (
                                <button
                                    type="button"
                                    className="rs-labelpick__manage qm-inline-action-btn"
                                    onClick={() => { close(false); onManage(); }}
                                >
                                    Manage labels...
                                </button>
                            )}
                        </div>
                    )}
                </div>
                </OperationalFieldBoundary>,
                document.body,
            )}
        </>
    );
}
